import React from "react";
import { t } from "ttag";

import { CSRF_HEADER, ajax, getCSRFToken } from "../../../utils/ajax";
import { money } from "../../../utils/format";
import { ICustomerDetails, IOrder } from "../models";
import { SelectInput } from "./blocks/SelectInput";

interface IProps extends IOrder {
    requestReload: () => void;
}

interface ICreditAssignErrors {
    [field: string]: string[];
}

const loadCSRs = async (): Promise<ICustomerDetails[]> => {
    const resp = await ajax
        .get("/api/csr/users/csrs/")
        .set("Accept", "application/json");
    return resp.body;
};

const assignCreditToCSR = async (
    orderUrl: string,
    csrId: string,
): Promise<void> => {
    const data = { csr: csrId };
    await ajax
        .post(`${orderUrl}give_credit/`)
        .set(CSRF_HEADER, await getCSRFToken())
        .send(data);
};

const buildOrderLines = (lines: IOrder["lines"]): React.JSX.Element[] => {
    return lines.map((line) => {
        return (
            <tr key={line.id}>
                <td>{line.id}</td>
                <td>{line.title}</td>
                <td>{line.partner_sku || "-"}</td>
                <td>{line.quantity}</td>
                <td>{line.status}</td>
                <td>{money(line.line_price_excl_tax)}</td>
                <td>{money(line.line_price_incl_tax)}</td>
                <td></td>
            </tr>
        );
    });
};

const buildCSROptions = (csrs: ICustomerDetails[]): [string, string][] => {
    return csrs.map((csr): [string, string] => {
        return [
            `${csr.id}`,
            `${csr.first_name} ${csr.last_name} <${csr.email}>`,
        ];
    });
};

export const CustomerDetailsOrder = (props: IProps): React.ReactNode => {
    const [csrs, setCSRs] = React.useState<ICustomerDetails[]>([]);
    const [creditAssignCSR, setCreditAssignCSR] = React.useState<string>("");
    const [creditAssignErrors, setCreditAssignErrors] =
        React.useState<ICreditAssignErrors>({});

    React.useEffect(() => {
        const fetchCSRs = async () => {
            const data = await loadCSRs();
            setCSRs(data);
            setCreditAssignCSR(`${data[0].id}`);
        };
        fetchCSRs();
    }, []);

    const onAssignCredit = async (
        event: React.FormEvent<HTMLElement>,
    ): Promise<void> => {
        event.preventDefault();
        try {
            await assignCreditToCSR(props.url, creditAssignCSR);
            props.requestReload();
        } catch (err) {
            setCreditAssignErrors(err.response.body);
        }
    };

    const handleSelectCSR = (_: string, value: string) => {
        setCreditAssignCSR(value);
    };

    const placed = new Date(props.date_placed).toLocaleString();
    const creditErrors = (creditAssignErrors.non_field_errors || []).map(
        (err, i) => {
            return <li key={i}>{err}</li>;
        },
    );
    const csrOptions = buildCSROptions(csrs);

    return (
        <div className="csr-customer-order">
            <div className="csr-customer-order__basic">
                <table>
                    <thead>
                        <tr>
                            <th colSpan={2}>{t`Basic Details`}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>{t`Order Number`}</th>
                            <td>{props.number}</td>
                        </tr>
                        <tr>
                            <th>{t`Status`}</th>
                            <td>{props.status}</td>
                        </tr>
                        <tr>
                            <th>{t`Shipping`}</th>
                            <td>{props.shipping_incl_tax}</td>
                        </tr>
                        <tr>
                            <th>{t`Total (excl. Tax)`}</th>
                            <td>{money(props.total_excl_tax)}</td>
                        </tr>
                        <tr>
                            <th>{t`Total (incl. Tax)`}</th>
                            <td>{money(props.total_incl_tax)}</td>
                        </tr>
                        <tr>
                            <th>{t`Date Placed`}</th>
                            <td>{placed}</td>
                        </tr>
                    </tbody>
                </table>
                <div className="button-group">
                    <a href={props.dashboard_link}>
                        {t`View Order in Dashboard`}
                    </a>
                </div>
            </div>
            <div className="csr-customer-order__credit">
                <h2>{t`Order Placement Credit`}</h2>
                <form onSubmit={onAssignCredit}>
                    <p>
                        {t`Use the form below to assign credit for this order to another customer service representative.`}
                    </p>
                    <ul className="error-list">{creditErrors}</ul>
                    <SelectInput
                        name="order_credit_select"
                        label={t`Select a customer service representative.`}
                        errors={creditAssignErrors.csr}
                        options={csrOptions}
                        value={creditAssignCSR}
                        onChange={handleSelectCSR}
                        isRequired={true}
                    />
                    <button type="submit">{t`Assign Order Credit`}</button>
                </form>
            </div>
            <div className="csr-customer-order__assistance">
                <h2>{t`Assistance Log`}</h2>
                <p>{t`Only the latest 10 log entries are shown.`}</p>
                <table>
                    <thead>
                        <tr>
                            <th>{t`Customer Service Representative`}</th>
                            <th>{t`Description`}</th>
                            <th>{t`Date`}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.assistance_log.length === 0 ? (
                            <tr>
                                <td colSpan={3}>
                                    <em>
                                        {t`No log entries found for this customer.`}
                                    </em>
                                </td>
                            </tr>
                        ) : null}
                        {props.assistance_log.map((entry) => (
                            <tr key={entry.id}>
                                <td>
                                    {entry.csr.first_name} {entry.csr.last_name}{" "}
                                    &lt;
                                    {entry.csr.email}&gt;
                                </td>
                                <td>{entry.message}</td>
                                <td>
                                    {new Date(
                                        entry.action_time,
                                    ).toLocaleString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="csr-customer-order__lines">
                <h2>Lines</h2>
                <table>
                    <thead>
                        <tr>
                            <th>{t`Line ID`}</th>
                            <th>{t`Product`}</th>
                            <th>{t`SKU`}</th>
                            <th>{t`Quantity`}</th>
                            <th>{t`Status`}</th>
                            <th>{t`Line Price (excl. Tax)`}</th>
                            <th>{t`Line Price (incl. Tax)`}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>{buildOrderLines(props.lines)}</tbody>
                </table>
            </div>
        </div>
    );
};
